/**
 * Drop countdown.
 *
 * Counts down to the date in `data-drop-date`, then swaps to the "live" state.
 * The target is rendered server-side as an ISO string; all arithmetic happens
 * client-side against the viewer's clock.
 *
 * Markup contract (see sections/drop-countdown.liquid):
 *   [data-drop-date]   ISO 8601 target, e.g. "2026-09-01T18:00"
 *   [data-unit="days"] etc. — the numeric cells to fill
 *   [data-state="counting"|"live"] — swapped when the target passes
 */
class DropCountdown extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    const raw = this.dataset.dropDate;
    if (!raw) return;

    // Treat a bare "YYYY-MM-DDTHH:MM" as local time, which is what a merchant
    // typing a date into the theme editor means.
    this.target = new Date(raw).getTime();
    if (Number.isNaN(this.target)) return;

    this.cells = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };

    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    if (this.#timer) window.clearInterval(this.#timer);
  }

  #tick() {
    const remaining = this.target - Date.now();

    if (remaining <= 0) {
      this.dataset.state = 'live';
      if (this.#timer) window.clearInterval(this.#timer);
      return;
    }

    const seconds = Math.floor(remaining / 1000);
    this.#set('days', Math.floor(seconds / 86400));
    this.#set('hours', Math.floor((seconds % 86400) / 3600));
    this.#set('minutes', Math.floor((seconds % 3600) / 60));
    this.#set('seconds', seconds % 60);
  }

  /**
   * @param {'days'|'hours'|'minutes'|'seconds'} unit
   * @param {number} value
   */
  #set(unit, value) {
    const cell = this.cells[unit];
    if (!cell) return;
    const text = unit === 'days' ? String(value) : String(value).padStart(2, '0');
    if (cell.textContent !== text) cell.textContent = text;
  }
}

if (!customElements.get('drop-countdown')) {
  customElements.define('drop-countdown', DropCountdown);
}
